import React from "react";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";

type PublicShareActionsProps = {
  title?: string;
  description?: string;
  message: string;
  shareUrl: string;
};

function fallbackCopyText(text: string) {
  if (typeof document === "undefined") return;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

async function copyText(text: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  fallbackCopyText(text);
}

export default function PublicShareActions({
  title = "Copy and share",
  description = "Use this message when you post in WhatsApp, Facebook, text, or email.",
  message,
  shareUrl,
}: PublicShareActionsProps) {
  const [copiedState, setCopiedState] = React.useState<"message" | "link" | null>(null);

  React.useEffect(() => {
    if (!copiedState) return undefined;
    const timer = window.setTimeout(() => setCopiedState(null), 2500);
    return () => window.clearTimeout(timer);
  }, [copiedState]);

  const handleCopyMessage = React.useCallback(async () => {
    await copyText(message);
    setCopiedState("message");
  }, [message]);

  const handleCopyLink = React.useCallback(async () => {
    await copyText(shareUrl);
    setCopiedState("link");
  }, [shareUrl]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "rgba(248, 251, 253, 0.96)",
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#16354c" }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(20, 50, 74, 0.08)",
          }}
        >
          <Typography variant="body2" sx={{ color: "#23445d", whiteSpace: "pre-wrap" }}>
            {message}
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button type="button" variant="contained" onClick={handleCopyMessage}>
            Copy Message
          </Button>
          <Button type="button" variant="outlined" onClick={handleCopyLink}>
            Copy Link
          </Button>
        </Stack>

        {copiedState ? (
          <Alert severity="success" sx={{ py: 0 }}>
            {copiedState === "message" ? "Message copied." : "Link copied."}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
